import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Search, SearchProps, SearchResultData } from 'semantic-ui-react';
import { debounce } from 'helpers';
import { searchOccupation } from 'services';
import { IFormFieldProps } from './common';
import InputWrap from './InputWrap';
import styles from './styles/TextInput.module.scss';
import classnames from 'classnames';
import * as R from 'ramda'; // Import Ramda functions as needed

interface ISearchInputProps {
  api?: keyof typeof searchApiMap;
}

const searchApiMap = {
  occupation: searchOccupation,
};

const SearchInput = ({
  api,
  basic,
  label,
  labelAfter,
  className,
  inputClassName,
  labelClassName,
  leftIcon,
  rightIcon,
  error,
  scrollIntoView,
  tooltip,
  showButton,
  buttonText = 'Search',
  onButtonClick,
  onChange,
  value,
  name,
  ...inputProps
}: IFormFieldProps & ISearchInputProps & React.HTMLProps<HTMLInputElement>) => {
  const [inputValue, setInputValue] = useState<string>(value || '');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState<boolean>(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const debounceSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        setSearching(true);
        const apiFn = api ? searchApiMap[api] : null;

        if (apiFn) {
          const res = await apiFn(term);
          setResults(res.data.map((r: any) => ({ ...r, title: r.label })));
        }
        setSearching(false);
      }, 500),
    [api],
  );

  const handleSearchChange = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>, data: SearchProps) => {
      const term = data.value || '';
      setInputValue(term);
      if (term.length < (data.minCharacters || 3)) {
        setResults([]);
        return;
      }
      debounceSearch(term);
    },
    [debounceSearch],
  );

  const handleSearchSelect = useCallback(
    (e: React.SyntheticEvent<HTMLElement>, data: SearchResultData) => {
      onChange?.({
        target: {
          name,
          value: data.result.title,
          result: data.result,
        },
      });

      // Blur input on select
      document.getElementById(name)?.blur();
    },
    [name, onChange],
  );

  return (
    <InputWrap
      className={classnames(styles.search, className)}
      errorClassName={classnames(styles.error, basic && styles.basicError)}
      error={error}
      label={label}
      labelAfter={labelAfter}
      labelClassName={labelClassName}
      name={name}
      rightIcon={rightIcon}
      scrollIntoView={scrollIntoView}
      tooltip={tooltip}
    >
      <Search
        id={name}
        className={classnames(styles.searchInput, inputClassName)}
        loading={searching}
        minCharacters={3}
        results={results}
        showNoResults={false}
        onResultSelect={handleSearchSelect}
        onSearchChange={handleSearchChange}
        {...inputProps}
        value={inputValue}
      />
    </InputWrap>
  );
};

export default SearchInput;
